import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, Search, Sprout } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type { FarmDetail, InputCatalogueItem, StaffDetail } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

type PlotCandidate = {
  plotId: string;
  plotName: string;
  locality?: string;
  farmerId: string;
  farmerName: string;
  farmId: string;
  farmName: string;
};

export default function SeedlingDistributionPlanFormPage() {
  const navigate = useNavigate();
  const [farms, setFarms] = useState<FarmDetail[]>();
  const [staff, setStaff] = useState<StaffDetail[]>();
  const [catalogue, setCatalogue] = useState<InputCatalogueItem[]>();
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedPlots, setSelectedPlots] = useState<string[]>([]);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    title: "",
    plannedStartDate: "",
    plannedEndDate: "",
    responsibleEmployeeId: "",
    seedlingItemId: "",
    notes: "",
  });

  useEffect(() => {
    let active = true;
    Promise.all([
      services.farms.list({ pageSize: 100 }),
      services.staff.list({ pageSize: 100 }),
      services.inputs.listCatalogue({ pageSize: 100 }),
    ])
      .then(([farmResult, staffResult, catalogueResult]) => {
        if (!active) return;
        setFarms(farmResult.items);
        setStaff(staffResult.items.filter((member) => member.employmentStatus === "ACTIVE"));
        setCatalogue(catalogueResult.items);
      })
      .catch((reason: Error) => {
        if (active) setError(reason.message || "Seedling Distribution planning data could not be loaded.");
      });
    return () => { active = false; };
  }, []);

  const nurseryItems = useMemo(
    () => (catalogue || []).filter((item) => item.status === "ACTIVE" && item.stockTrackingMode === "NURSERY"),
    [catalogue],
  );

  const plotCandidates = useMemo<PlotCandidate[]>(() => {
    const rows: PlotCandidate[] = [];
    (farms || [])
      .filter((farm) => farm.farmStatus === "ACTIVE")
      .forEach((farm) => {
        (farm.plotsData || [])
          .filter((plot) => plot.status === "ACTIVE")
          .forEach((plot) => rows.push({
            plotId: plot.id,
            plotName: plot.name,
            locality: plot.localityName,
            farmerId: farm.farmerId,
            farmerName: farm.farmerName,
            farmId: farm.id,
            farmName: farm.name,
          }));
      });
    return rows.sort((a, b) =>
      a.farmerName.localeCompare(b.farmerName) || a.farmName.localeCompare(b.farmName) || a.plotName.localeCompare(b.plotName),
    );
  }, [farms]);

  const visiblePlots = useMemo(
    () => plotCandidates.filter((row) =>
      `${row.farmerName} ${row.farmName} ${row.plotName} ${row.locality || ""}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    ),
    [plotCandidates, search],
  );

  const selectedRows = useMemo(
    () => plotCandidates.filter((row) => selectedPlots.includes(row.plotId)),
    [plotCandidates, selectedPlots],
  );

  const totalPlanned = selectedRows.reduce((sum, row) => sum + (Number(quantities[row.plotId]) || 0), 0);

  const togglePlot = (plotId: string, checked: boolean) => {
    setSelectedPlots((current) =>
      checked ? Array.from(new Set([...current, plotId])) : current.filter((id) => id !== plotId),
    );
    if (!checked) {
      setQuantities((current) => {
        const next = { ...current };
        delete next[plotId];
        return next;
      });
    }
  };

  const selectVisible = () => {
    setSelectedPlots((current) => Array.from(new Set([...current, ...visiblePlots.map((row) => row.plotId)])));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.title.trim() || !form.plannedStartDate || !form.plannedEndDate || !form.responsibleEmployeeId || !form.seedlingItemId) {
      toast.error("Complete the Plan title, dates, responsible staff and seedling item.");
      return;
    }
    if (form.plannedEndDate < form.plannedStartDate) {
      toast.error("Plan end date cannot be before the start date.");
      return;
    }
    if (selectedRows.length === 0) {
      toast.error("Select at least one farmer Plot for the Seedling Distribution Plan.");
      return;
    }
    const invalid = selectedRows.filter((row) => !quantities[row.plotId] || Number(quantities[row.plotId]) <= 0);
    if (invalid.length) {
      toast.error("Every selected Plot needs a planned seedling quantity.");
      return;
    }

    setWorking(true);
    try {
      const plan = await services.nursery.createDistributionPlan({
        title: form.title.trim(),
        plannedStartDate: form.plannedStartDate,
        plannedEndDate: form.plannedEndDate,
        responsibleEmployeeId: form.responsibleEmployeeId,
        notes: form.notes.trim() || undefined,
        targets: selectedRows.map((row) => ({
          farmerId: row.farmerId,
          farmId: row.farmId,
          plotId: row.plotId,
          seedlingItemId: form.seedlingItemId,
          plannedQuantity: Number(quantities[row.plotId]),
        })),
      });
      toast.success("Seedling Distribution Plan created", {
        description: `${selectedRows.length} Plot allocation${selectedRows.length === 1 ? "" : "s"} · ${totalPlanned.toLocaleString()} seedlings planned.`,
      });
      navigate(`/distributions/seedlings/plans/${plan.id}`);
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Seedling Distribution Plan could not be created.");
    } finally {
      setWorking(false);
    }
  };

  if (error) return <ErrorState message={error} />;
  if (!farms || !staff || !catalogue) return <LoadingState label="Preparing Seedling Distribution Plan" />;

  return (
    <div className="space-y-6" data-testid="seedling-distribution-plan-form">
      <PageHeader
        eyebrow="Distributions · Seedlings"
        title="New Seedling Distribution Plan"
        description="Plan many Farmer → Farm → Plot allocations at once. Variety detail is not required at planning or issue time."
        backTo="/distributions"
      />

      <form onSubmit={submit} className="space-y-5">
        <section className="form-section">
          <h2 className="text-lg font-semibold">Distribution programme</h2>
          <p className="mt-1 text-sm text-muted-foreground">Define the batch programme once, then allocate quantities to many Plots below.</p>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Plan title</Label>
              <Input value={form.title} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm((v) => ({ ...v, title: e.target.value }))} placeholder="e.g. Serere seedling distribution — October 2026" />
            </div>
            <div className="space-y-2">
              <Label>Responsible staff</Label>
              <Select value={form.responsibleEmployeeId} onValueChange={(value: string) => setForm((v) => ({ ...v, responsibleEmployeeId: value }))}>
                <SelectTrigger><SelectValue placeholder="Choose responsible staff" /></SelectTrigger>
                <SelectContent>{staff.map((member) => <SelectItem key={member.id} value={member.id}>{member.name} · {member.projectAssignment?.roleOnProject || member.jobTitle}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Seedling catalogue item</Label>
              <Select value={form.seedlingItemId} onValueChange={(value: string) => setForm((v) => ({ ...v, seedlingItemId: value }))}>
                <SelectTrigger><SelectValue placeholder="Choose Nursery-tracked seedlings" /></SelectTrigger>
                <SelectContent>{nurseryItems.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Start date</Label><Input type="date" value={form.plannedStartDate} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm((v) => ({ ...v, plannedStartDate: e.target.value }))} /></div>
            <div className="space-y-2"><Label>End date</Label><Input type="date" value={form.plannedEndDate} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm((v) => ({ ...v, plannedEndDate: e.target.value }))} /></div>
            <div className="space-y-2 md:col-span-2"><Label>Plan note</Label><Textarea value={form.notes} onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setForm((v) => ({ ...v, notes: e.target.value }))} placeholder="Optional programme instruction" /></div>
          </div>
        </section>

        <section className="form-section">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold"><Sprout className="size-5 text-primary" />Farmer Plot allocations</h2>
              <p className="mt-1 text-sm text-muted-foreground">Select Plots in bulk. The planned quantity is a total seedlings figure; variety composition can remain unknown.</p>
            </div>
            <div className="text-right"><p className="text-2xl font-semibold text-primary">{selectedRows.length}</p><p className="text-xs text-muted-foreground">Plots · {totalPlanned.toLocaleString()} seedlings</p></div>
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} className="pl-9" placeholder="Search farmer, Farm, Plot or locality" />
            </div>
            <Button type="button" variant="outline" onClick={selectVisible}>Select visible</Button>
          </div>

          <div className="mt-4 max-h-[520px] space-y-2 overflow-y-auto pr-1">
            {visiblePlots.map((row) => {
              const checked = selectedPlots.includes(row.plotId);
              return (
                <div key={row.plotId} className={`grid gap-3 rounded-xl border p-4 md:grid-cols-[auto_1.4fr_1fr_0.7fr] md:items-center ${checked ? "border-primary/30 bg-primary/[0.035]" : ""}`}>
                  <input type="checkbox" className="size-4" checked={checked} onChange={(e: ChangeEvent<HTMLInputElement>) => togglePlot(row.plotId, e.target.checked)} />
                  <div>
                    <p className="font-semibold">{row.farmerName}</p>
                    <p className="text-xs text-muted-foreground">{row.farmName}</p>
                  </div>
                  <div>
                    <p className="font-medium">{row.plotName}</p>
                    <p className="text-xs text-muted-foreground">{row.locality || "Locality not recorded"}</p>
                  </div>
                  <Input
                    type="number"
                    min="1"
                    disabled={!checked}
                    value={quantities[row.plotId] || ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setQuantities((current) => ({ ...current, [row.plotId]: e.target.value }))}
                    placeholder="Seedlings"
                  />
                </div>
              );
            })}
            {visiblePlots.length === 0 && <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">No active Plots match this search.</div>}
          </div>
        </section>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/distributions")}>Cancel</Button>
          <Button type="submit" disabled={working || nurseryItems.length === 0}>
            <CalendarPlus className="mr-2 size-4" />
            {working ? "Creating Plan..." : `Create Draft Plan (${selectedRows.length} Plots)`}
          </Button>
        </div>
      </form>
    </div>
  );
}
